import classNames from "classnames";
import React, { PureComponent } from "react";
import { PortalSettingsStateContext } from "../../contexts/portal-setting-context-provider";
import { Portal } from "../portal/portal";
import { CSS_CLASSES } from "./overlay-css-classes";
import { OverlayProps, OverlayState } from "./overlay.types";

/**
 * The `Overlay` component is a low-level atomic component that can be used to render content _on top of_ other content.
 *
 * It uses the `Portal` component to render its content outside of DOM sub-tree that is controlled by React thus
 * allowing a robust way to overlay content on top of React application content, without having to worry about z-index
 * ordering or clipping containers. It honors a global **portal disabled** setting provided by the
 * `PortalSettingsContextProvider` to control this behavior for all overlay based component across an application. It
 * present, this setting can be overridden per overlay instance using the `usePortal` prop.
 *
 * It provides an optional backdrop element that's rendered behind the overlaid content and covers the entire parent
 * element of the overlay. This can be used achieve a _modal_ behavior by blocking user interaction with the rest of
 * application while the overlay is open.
 *
 * `Overlay` forms the foundation for other components - `Popover`, `Dialog` and `Notification`.
 * While these higher level components cover a large set of application use cases for an overlay, it can certainly be
 * used directly when creating a different or more custom experience.
 */
export class Overlay extends PureComponent<OverlayProps, OverlayState> {
  static defaultProps: Partial<OverlayProps> = {
    alwaysRenderContent: false,
    makeOthersInert: true,
    closeOnEscapeKey: true,
    closeOnOutsideClick: true,
    usePortal: true,
    lazyPortal: true,
    showBackdrop: true,
    clipContainer: false,
    scrollContainer: false,
    preserveDomElement: false,
  };

  static getDerivedStateFromProps({ open: openedOnce }: OverlayProps) {
    if (openedOnce) {
      return { openedOnce };
    }
    return null;
  }

  static contextType = PortalSettingsStateContext;
  static openStack: Overlay[] = [];

  // Ref to the HTMLDivElement that contains the backdrop and overlay content
  containerElementRef = React.createRef<HTMLDivElement>();

  constructor(props?: OverlayProps) {
    super(props);
    this.state = { openedOnce: props.open };
  }

  componentDidMount() {
    if (this.props.open) {
      this.openOverlay();
    }
  }

  componentDidUpdate(prevProps: OverlayProps) {
    if (prevProps.open && !this.props.open) {
      this.closeOverlay();
    } else if (!prevProps.open && this.props.open) {
      this.openOverlay();
    }
  }

  private usePortal() {
    return this.context?.disabled === undefined ? this.props.usePortal : !this.context?.disabled;
  }

  componentWillUnmount() {
    this.closeOverlay();
  }

  openOverlay() {
    const { closeOnOutsideClick, showBackdrop } = this.props;
    const { openStack } = Overlay;
    openStack.push(this);

    if (closeOnOutsideClick && !showBackdrop) {
      window.addEventListener("mousedown", this.handleDocumentClick);
    }

    // disable body scrolling
    if (showBackdrop && this.usePortal()) {
      document.body.style.overflow = "hidden";
    }
  }

  closeOverlay() {
    window.removeEventListener("mousedown", this.handleDocumentClick);

    const { openStack } = Overlay;
    const stackIndex = openStack.indexOf(this);
    if (stackIndex !== -1) {
      openStack.splice(stackIndex, 1);
      // enable body scrolling if no portal open that requires disabling body scrolling
      if (openStack.filter((o) => o.usePortal() && o.props.showBackdrop).length === 0) {
        document.body.style.overflow = "";
      }
    }
  }

  handleBackdropMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const { closeOnOutsideClick, onClose } = this.props;
    if (closeOnOutsideClick && onClose) {
      onClose(e);
    }
  };

  handleDocumentClick = (e: { selectingOption?: boolean } & MouseEvent) => {
    const { closeOnOutsideClick, open, onClose } = this.props;
    const eventTarget = e.target as HTMLElement;

    const stackIndex = Overlay.openStack.indexOf(this);
    const clickedOutside = Overlay.openStack.slice(stackIndex).some(({ containerElementRef: { current: el } }) => {
      if (el) {
        const targetIsContainedWithinOverlayContainer = el.contains(eventTarget);

        /**
         * Logic to detect if the click was inside an element that is "logically" owned by an element within the overlay.
         * The "logical" ownership is established via the `aria-controls` attributes on any element within the overlay.
         * The attribute value points to the `id` of an element that it controls. This "logically" owned element could be
         * anywhere in the document, not necessarily within the overlay container.
         */
        let targetIsAriaOwnedByElementWithinOverlayContainer = false;
        const elementWithAriaOwns = el.querySelectorAll("[aria-controls]");
        if (elementWithAriaOwns.length > 0) {
          const ariaOwnedElementIds = Array.from(elementWithAriaOwns).map((e) => e?.getAttribute("aria-controls"));
          const ariaOwnedElements = ariaOwnedElementIds.map((id) => id && document.getElementById(id));
          targetIsAriaOwnedByElementWithinOverlayContainer = ariaOwnedElements.some((e) => e?.contains(eventTarget));
        }

        const clickedOnOverlayContent =
          targetIsContainedWithinOverlayContainer || targetIsAriaOwnedByElementWithinOverlayContainer;

        const clickedOnOverlayContainer = el.isSameNode(eventTarget);

        // Clicking somewhere other than the overlay content OR Clicking the overlay container = Clicking "outside".
        return !clickedOnOverlayContent || clickedOnOverlayContainer;
      } else {
        return false;
      }
    });

    /**
     * The `!e.selectingOption` is checked as a special case for the `Select` component,
     * when it is used within another Overlay, e.g. within a `Dialog`.
     * By default the `Select` component uses a "portaled" `Overlay` (via `Popover`) to render it's dropdown menu.
     * So when it is used within another overlay, and the dropdown is open, then there are two overlays open - select's
     * parent overlay and select's dropdown menu.
     * Now, when an item in select's dropdown menu is clicked, it is treated as being inside the dropdown menu overlay,
     * BUT as being outside the parent overlay. Thus
     *
     */
    if (open && closeOnOutsideClick && clickedOutside && onClose && !e.selectingOption) {
      onClose(e as any);
    }
  };

  handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const { closeOnEscapeKey, onClose } = this.props;
    if (closeOnEscapeKey && e.key === "Escape" && onClose) {
      e.preventDefault();
      onClose(e);
    }
  };

  render() {
    const {
      open,
      alwaysRenderContent,
      makeOthersInert,
      closeOnOutsideClick,
      portalClassName,
      portalContainer,
      portalChildrenDidMount,
      lazyPortal,
      showBackdrop,
      backdropClassName,
      clipContainer,
      scrollContainer,
      className,
      children,
      rootProps,
      preserveDomElement,
    } = this.props;

    // If lazy and not yet opened even once, then don't render yet
    if (!preserveDomElement && lazyPortal && !this.state.openedOnce) {
      return null;
    }

    // Overlay Backdrop
    const overlayBackdropClassName = classNames(CSS_CLASSES.OVERLAY_BACKDROP, backdropClassName);
    const overlayBackdrop = (
      <div
        className={overlayBackdropClassName}
        onMouseDown={this.handleBackdropMouseDown}
        tabIndex={closeOnOutsideClick ? 0 : null}
      />
    );

    // Overlay Content
    const overlayContent = React.cloneElement(children, {
      className: classNames(children.props.className, CSS_CLASSES.OVERLAY_CONTENT),
    });

    // Overlay Container
    const overlayRootClassName = classNames(
      CSS_CLASSES.OVERLAY_ROOT,
      {
        [CSS_CLASSES.OVERLAY_OPEN]: open && makeOthersInert,
        [CSS_CLASSES.OVERLAY_INLINE]: !this.usePortal() || portalContainer,
        [CSS_CLASSES.OVERLAY_CLIP_CONTAINER]: clipContainer,
        [CSS_CLASSES.OVERLAY_SCROLL_CONTAINER]: scrollContainer,
      },
      className
    );

    const overlay = (
      <div
        className={overlayRootClassName}
        onKeyDown={this.handleKeyDown}
        ref={this.containerElementRef}
        {...rootProps}
        style={
          preserveDomElement && !(open || alwaysRenderContent)
            ? { ...rootProps?.style, display: "none" }
            : rootProps?.style
        }
      >
        {open && showBackdrop && overlayBackdrop}
        {(preserveDomElement || open || alwaysRenderContent) && overlayContent}
      </div>
    );
    // Mount within a Portal if asked for, otherwise simply render
    if (this.usePortal()) {
      return (
        <Portal className={portalClassName} container={portalContainer} childrenDidMount={portalChildrenDidMount}>
          {overlay}
        </Portal>
      );
    } else {
      return overlay;
    }
  }
}
